import { useQuery } from '@tanstack/vue-query'
import type { BlogPost } from '~/types/blog-post'
import type { Database } from '~/types'
import type { Video, YoutubeVideo } from '~/types/videos'

export const useHomePageData = () => {
  const supabase = useSupabaseClient<Database>()

  // Fetch latest article
  const { data: featuredArticle } = useQuery({
    queryKey: ['featuredArticle'],
    queryFn: async () => {
      const articles = await queryContent<BlogPost>('/articles')
        .where({ _extension: 'md' })
        .sort({ date: -1 })
        .limit(1)
        .find()
      return articles[0]
    }
  })

  // Fetch next 4 articles
  const { data: articles } = useQuery({
    queryKey: ['articles'],
    queryFn: async () => {
      const articles = await queryContent<BlogPost>('/articles')
        .where({ _extension: 'md' })
        .sort({ date: -1 })
        .skip(1) // Skip the featured article
        .limit(4)
        .find()
      return articles
    }
  })

  // Fetch YouTube videos
  const { data: youtubeVideos } = useQuery({
    queryKey: ['youtubeVideos'],
    queryFn: async () => {
      const youtubeVideos = await queryContent<BlogPost>('/youtube-videos')
        .where({ _extension: 'md' })
        .sort({ date: -1 })
        .limit(8)
        .find()
      return youtubeVideos
    }
  })

  // Fetch latest 4 tweets (excluding retweets and replies)
  const { data: tweets } = useQuery({
    queryKey: ['tweets'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tweets')
        .select('id, text, entities, tweet_created_at, tweet_id::text, twitter_user_id, created_at, public_retweet_count, public_reply_count, public_like_count, public_quote_count, public_bookmark_count, public_impression_count, conversation_id, referenced_tweets, in_reply_to_user_id, attachments')
        .order('tweet_created_at', { ascending: false })

      if (error) throw error
      const filtered = data.filter(tweet => !(tweet.referenced_tweets as Array<{ type: string }>)?.some(
        ref => ref.type === 'retweeted' || ref.type === 'replied_to'
      ))
      return filtered.slice(0, 4)
    }
  })

  // Fetch latest 4 Instagram videos
  const { data: instagramVideos } = useQuery({
    queryKey: ['instagramVideos'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('instagram_media')
        .select('*')
        .eq('media_type', 'VIDEO')
        .order('created_at', { ascending: false })
        .limit(4)

      if (error) throw error
      return data
    }
  })

  // Fetch latest 4 Instagram posts (excluding videos)
  const { data: instagramPosts } = useQuery({
    queryKey: ['instagramPosts'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('instagram_media')
        .select('*')
        .neq('media_type', 'VIDEO')
        .order('timestamp', { ascending: false })
        .limit(4)

      if (error) throw error
      return data
    }
  })

  // Computed property that combines YouTube and Instagram videos
  const videos = computed<Video[]>(() => {
    const youtubeEntries: Video[] = (youtubeVideos.value || []).map(video => ({
      type: 'youtube',
      video: {
        ...video,
        path: video.path
      } as YoutubeVideo
    }))

    const instagramEntries: Video[] = (instagramVideos.value || []).map(video => ({
      type: 'instagram',
      video: video
    }))

    // Combine and limit to 4 total videos
    return [...youtubeEntries, ...instagramEntries]
      .sort((a, b) => {
        const dateA = a.type === 'instagram'
          ? new Date(a.video.created_at).getTime()
          : new Date(a.video.createdAt).getTime()
        const dateB = b.type === 'instagram'
          ? new Date(b.video.created_at).getTime()
          : new Date(b.video.createdAt).getTime()
        return dateB - dateA
      })
      .slice(0, 4)
  })

  return {
    featuredArticle,
    articles,
    tweets,
    instagramPosts,
    videos
  }
} 